// Svelte-action интерактивного горизонтального свайпа (этап 10: чат ↔ контекст ↔ список).
//
// use:swipe={{ directions: 'both' | 'left' | 'right', startThreshold: 12, axisRatio: 1.5,
//              edgeGuard: 24, minCommit: 70, commitRatio: 0.3, velocityThreshold: 0.5,
//              resistance: 0.35, ignore: IGNORE_SELECTOR }}
// ignore — где жест не начинается (1.27.3: строка списка чатов — ссылка, и свайп по ней
// должен работать, поэтому там `a` из исключений убран).
//
// Жест «живой», как системная навигация iOS: экран идёт за пальцем, при отпускании либо
// доезжает, либо возвращается. События на node (все — CustomEvent с detail):
//   swipestart {dx: 0}                       — ось захвачена как горизонтальная;
//   swipemove  {dx, progress}                — палец движется (не чаще кадра, rAF);
//   swipeend   {dx, velocity, committed, direction} — палец отпущен (или pointercancel);
//   swipeleft / swiperight {dx, velocity}    — жест «зачтён» (перед swipeend).
// «Зачтён» — если |dx| ≥ max(minCommit, ширина·commitRatio) ИЛИ отпустили быстро
// (|velocity| ≥ velocityThreshold px/мс при |dx| ≥ 24 px) в ту же сторону.
// Направление без назначения (directions) не запрещено, а «резиновое»: dx умножается
// на resistance — палец чувствует, что экран дальше не идёт.
//
// Pointer Events (единая обработка мыши/пальца), как у longpress. Ось захватывается один
// раз после startThreshold px: вертикаль (скролл ленты) — жест игнорируется до отпускания;
// горизонталь — pointer capture, чтобы уход пальца с элемента не рвал жест. Не считаются
// жестом: старт на полях ввода/кнопках/ссылках/[data-no-swipe], старт внутри горизонтально
// прокручиваемого блока (таблица, код на десктопе — жест отдаётся его нативной прокрутке)
// и старт ближе edgeGuard px к левой кромке окна — это системный back-swipe Safari. Контейнеру нужен
// `touch-action: pan-y`, иначе iOS отдаёт горизонталь браузеру. Клик после зачтённого
// жеста гасится (как у longpress), чтобы отпускание над кнопкой её не нажимало.

const IGNORE_SELECTOR = 'input, textarea, select, button, a, [data-no-swipe]';
const VELOCITY_WINDOW_MS = 100;
const MIN_VELOCITY_DISTANCE = 24;
const MIN_VELOCITY_DT = 16;
const CLICK_SUPPRESS_MS = 300;

function now() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function schedule(callback) {
  if (typeof requestAnimationFrame === 'function') {
    return requestAnimationFrame(callback);
  }
  return setTimeout(callback, 16);
}

function unschedule(handle) {
  if (typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(handle);
  } else {
    clearTimeout(handle);
  }
}

// Старт на горизонтально прокручиваемом блоке (таблица, код на десктопе): жест целиком
// отдаётся нативной прокрутке блока. Иначе экран уезжал вместе с прокруткой, а на iOS
// захват указателя поверх нативного скролла плюс transform всей страницы давали пик памяти.
function startsInsideHorizontalScroller(target, boundary) {
  let element = target;
  while (element && element !== boundary && element.nodeType === 1) {
    if (element.scrollWidth > element.clientWidth + 1 && typeof getComputedStyle === 'function') {
      const overflowX = getComputedStyle(element).overflowX;
      if (overflowX === 'auto' || overflowX === 'scroll') {
        return true;
      }
    }
    element = element.parentElement;
  }
  return false;
}

export function swipe(node, options = {}) {
  let opts = options || {};

  let tracking = false;
  let axis = null; // null — ось ещё не захвачена, 'x' | 'y'
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let lastDx = 0;
  let samples = []; // [{t, x}] за последние VELOCITY_WINDOW_MS — для скорости
  let frame = null;
  let scheduled = false; // отдельно от frame: синхронный rAF (тесты) вызывает flush до присваивания
  let pendingMove = null;
  let suppressClickUntil = 0;

  function allowed(direction) {
    const directions = opts.directions ?? 'both';
    return directions === 'both' || directions === direction;
  }

  function resist(dx) {
    if (dx === 0) {
      return 0;
    }
    const direction = dx < 0 ? 'left' : 'right';
    if (allowed(direction)) {
      return dx;
    }
    return dx * (opts.resistance ?? 0.35);
  }

  function commitDistance() {
    const width = node.getBoundingClientRect?.().width || node.offsetWidth || 0;
    return Math.max(opts.minCommit ?? 70, width * (opts.commitRatio ?? 0.3));
  }

  function velocityOf() {
    if (samples.length < 2) {
      return 0;
    }
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = last.t - first.t;
    if (dt < MIN_VELOCITY_DT) {
      return 0;
    }
    return (last.x - first.x) / dt;
  }

  function emit(type, detail) {
    node.dispatchEvent(new CustomEvent(type, { detail }));
  }

  function unscheduleMove() {
    if (frame !== null) {
      unschedule(frame);
    }
    frame = null;
    scheduled = false;
  }

  function reset() {
    tracking = false;
    axis = null;
    pointerId = null;
    lastDx = 0;
    samples = [];
    pendingMove = null;
    unscheduleMove();
  }

  function flushMove() {
    frame = null;
    scheduled = false;
    if (pendingMove === null || !tracking) {
      return;
    }
    const dx = pendingMove;
    pendingMove = null;
    const distance = commitDistance();
    emit('swipemove', { dx, progress: Math.min(1, Math.abs(dx) / distance) });
  }

  function onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    const edgeGuard = opts.edgeGuard ?? 24;
    if (event.clientX <= edgeGuard) {
      return;
    }
    const target = event.target;
    if (target && typeof target.closest === 'function' && target.closest(opts.ignore ?? IGNORE_SELECTOR)) {
      return;
    }
    if (target && startsInsideHorizontalScroller(target, node)) {
      return;
    }
    reset();
    tracking = true;
    pointerId = event.pointerId ?? null;
    startX = event.clientX;
    startY = event.clientY;
    samples = [{ t: now(), x: event.clientX }];
  }

  function onPointerMove(event) {
    if (!tracking) {
      return;
    }
    if (pointerId !== null && event.pointerId !== undefined && event.pointerId !== pointerId) {
      return;
    }
    const rawDx = event.clientX - startX;
    const dy = event.clientY - startY;

    if (axis === null) {
      const startThreshold = opts.startThreshold ?? 12;
      if (Math.abs(rawDx) < startThreshold && Math.abs(dy) < startThreshold) {
        return;
      }
      const axisRatio = opts.axisRatio ?? 1.5;
      axis = Math.abs(rawDx) > axisRatio * Math.abs(dy) ? 'x' : 'y';
      if (axis === 'x') {
        try {
          if (pointerId !== null && typeof node.setPointerCapture === 'function') {
            node.setPointerCapture(pointerId);
          }
        } catch {
          // jsdom / старые браузеры — без захвата тоже работает, пока палец над node.
        }
        emit('swipestart', { dx: 0 });
      }
    }
    if (axis !== 'x') {
      return;
    }

    const t = now();
    samples.push({ t, x: event.clientX });
    while (samples.length > 2 && t - samples[0].t > VELOCITY_WINDOW_MS) {
      samples.shift();
    }
    lastDx = resist(rawDx);
    pendingMove = lastDx;
    if (!scheduled) {
      scheduled = true;
      frame = schedule(flushMove);
    }
  }

  function finish(cancelled) {
    if (!tracking) {
      return;
    }
    const wasHorizontal = axis === 'x';
    const dx = lastDx;
    const velocity = cancelled ? 0 : velocityOf();
    // Последний кадр движения — до решения, чтобы потребитель не «отставал» от пальца.
    unscheduleMove();
    if (wasHorizontal && pendingMove !== null) {
      flushMove();
    }
    reset();
    if (!wasHorizontal) {
      return;
    }

    const direction = dx < 0 ? 'left' : 'right';
    let committed = false;
    if (!cancelled && dx !== 0 && allowed(direction)) {
      const byDistance = Math.abs(dx) >= commitDistance();
      const sameWay = Math.sign(velocity) === Math.sign(dx);
      const byVelocity = sameWay
        && Math.abs(velocity) >= (opts.velocityThreshold ?? 0.5)
        && Math.abs(dx) >= MIN_VELOCITY_DISTANCE;
      committed = byDistance || byVelocity;
    }
    if (committed) {
      suppressClickUntil = now() + CLICK_SUPPRESS_MS;
      emit(direction === 'left' ? 'swipeleft' : 'swiperight', { dx, velocity });
    }
    emit('swipeend', { dx, velocity, committed, direction });
  }

  function onPointerUp(event) {
    if (pointerId !== null && event.pointerId !== undefined && event.pointerId !== pointerId) {
      return;
    }
    finish(false);
  }

  function onPointerCancel() {
    finish(true);
  }

  function onClickCapture(event) {
    if (now() < suppressClickUntil) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  node.addEventListener('pointerdown', onPointerDown);
  node.addEventListener('pointermove', onPointerMove);
  node.addEventListener('pointerup', onPointerUp);
  node.addEventListener('pointercancel', onPointerCancel);
  node.addEventListener('click', onClickCapture, true);

  return {
    update(nextOptions = {}) {
      opts = nextOptions || {};
    },
    destroy() {
      reset();
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerCancel);
      node.removeEventListener('click', onClickCapture, true);
    },
  };
}
